<script setup lang="ts">
import QuoteSummaryCard from '@/components/quotes/QuoteSummaryCard.vue';
import CommercialDocumentDetailsSheet from '@/components/revenue/CommercialDocumentDetailsSheet.vue';
import { EmptyState } from '@/components/ui/empty-state';
import GroupedEntityList from '@/components/workspace/GroupedEntityList.vue';
import { FileText } from 'lucide-vue-next';
import { clientOwnershipLabel } from '~/lib/clientOwnership';
import type { Invoice, Quote } from '~/types/models';

const props = withDefaults(defineProps<{
    quotes: Quote[];
    showProject?: boolean;
    showClient?: boolean;
    canView?: boolean;
    canEdit?: boolean;
    canSend?: boolean;
    canAccept?: boolean;
    canReject?: boolean;
    canGenerateInvoices?: boolean;
    canDelete?: boolean;
    canCopyPublicLink?: boolean;
    projectNameById?: Record<string, string>;
    clientNameById?: Record<string, string>;
    quoteInvoicesByQuoteId?: Record<string, Invoice[]>;
    tenantSlug?: string;
    emptyTitle?: string;
    emptyDescription?: string;
}>(), {
    showProject: true,
    showClient: true,
    canView: true,
    canEdit: false,
    canSend: false,
    canAccept: false,
    canReject: false,
    canGenerateInvoices: false,
    canDelete: false,
    canCopyPublicLink: false,
    projectNameById: () => ({}),
    clientNameById: () => ({}),
    quoteInvoicesByQuoteId: () => ({}),
    tenantSlug: '',
    emptyTitle: 'No quotes found',
    emptyDescription: 'Create a quote or adjust the filters to see commercial work here.',
});

const emit = defineEmits<{
    view: [Quote];
    edit: [Quote];
    send: [string];
    remind: [string];
    accept: [string];
    'approve-internally': [string];
    reject: [string];
    'view-linked-invoice': [Invoice];
    'create-full-invoice': [string];
    'create-deposit-invoice': [string];
    'create-balance-invoice': [string];
    'create-next-installment-invoice': [string];
    'copy-public-link': [Quote];
    delete: [string];
}>();

function hasDeposit(quote: Quote): boolean {
    return Boolean(quote.deposit?.enabled && Number(quote.deposit.amount || 0) > 0);
}

function hasPaymentPlan(quote: Quote): boolean {
    return Boolean(quote.paymentPlan?.enabled && (quote.paymentPlan.installments?.length || 0) > 0);
}

function showFullInvoiceAction(quote: Quote): boolean {
    return !hasDeposit(quote) && !hasPaymentPlan(quote);
}

function showDepositInvoiceAction(quote: Quote): boolean {
    return hasDeposit(quote) && !hasPaymentPlan(quote);
}

function showBalanceInvoiceAction(quote: Quote): boolean {
    return hasDeposit(quote) && !hasPaymentPlan(quote);
}

function showNextInstallmentAction(quote: Quote): boolean {
    return hasPaymentPlan(quote);
}

function invoicesForQuote(quoteId: string): Invoice[] {
    return props.quoteInvoicesByQuoteId[quoteId] || [];
}

const selectedQuote = ref<Quote | null>(null);
const detailsOpen = computed({
    get: () => Boolean(selectedQuote.value),
    set: (value: boolean) => {
        if (!value) selectedQuote.value = null;
    },
});

function openDetails(quote: Quote) {
    selectedQuote.value = quote;
}

function viewSelectedQuote() {
    if (!selectedQuote.value) return;
    emit('view', selectedQuote.value);
    selectedQuote.value = null;
}

function editSelectedQuote() {
    if (!selectedQuote.value) return;
    emit('edit', selectedQuote.value);
    selectedQuote.value = null;
}

function copySelectedQuoteLink() {
    if (!selectedQuote.value) return;
    emit('copy-public-link', selectedQuote.value);
}

function viewSelectedLinkedInvoice(invoice: Invoice) {
    emit('view-linked-invoice', invoice);
    selectedQuote.value = null;
}

function clientLabel(quote: Quote): string {
    return clientOwnershipLabel(quote, {
        clientNameById: props.clientNameById,
    });
}

</script>

<template>
    <GroupedEntityList :empty="quotes.length === 0">
        <template #empty>
            <EmptyState :title="emptyTitle" :description="emptyDescription">
                <template #icon><FileText class="size-8" /></template>
            </EmptyState>
        </template>

        <QuoteSummaryCard
            v-for="quote in quotes"
            :key="quote.id"
            :quote="quote"
            :show-project="showProject"
            :show-client="showClient"
            :can-view="canView"
            :can-edit="canEdit"
            :can-send="canSend"
            :can-accept="canAccept"
            :can-reject="canReject"
            :can-generate-invoices="canGenerateInvoices"
            :can-delete="canDelete"
            :can-copy-public-link="canCopyPublicLink"
            :project-name="projectNameById[quote.projectId || ''] || ''"
            :client-name="clientLabel(quote)"
            :invoices="invoicesForQuote(quote.id)"
            surface="list"
            @view="emit('view', $event)"
            @details="openDetails"
            @edit="emit('edit', $event)"
            @send="emit('send', $event)"
            @remind="emit('remind', $event)"
            @accept="emit('accept', $event)"
            @approve-internally="emit('approve-internally', $event)"
            @reject="emit('reject', $event)"
            @view-linked-invoice="emit('view-linked-invoice', $event)"
            @create-full-invoice="emit('create-full-invoice', $event)"
            @create-deposit-invoice="emit('create-deposit-invoice', $event)"
            @create-balance-invoice="emit('create-balance-invoice', $event)"
            @create-next-installment-invoice="emit('create-next-installment-invoice', $event)"
            @copy-public-link="emit('copy-public-link', $event)"
            @delete="emit('delete', $event)"
        />
    </GroupedEntityList>

    <CommercialDocumentDetailsSheet
        v-if="selectedQuote"
        v-model:open="detailsOpen"
        kind="quote"
        :quote="selectedQuote"
        :client-name="clientLabel(selectedQuote)"
        :project-name="projectNameById[selectedQuote.projectId || ''] || ''"
        :linked-invoices="invoicesForQuote(selectedQuote.id)"
        :can-view="canView"
        :can-edit="canEdit && selectedQuote.status !== 'accepted'"
        :can-copy-public-link="canCopyPublicLink"
        @view="viewSelectedQuote"
        @edit="editSelectedQuote"
        @copy-public-link="copySelectedQuoteLink"
        @view-linked-invoice="viewSelectedLinkedInvoice"
    />
</template>
